/**
 * Base URL for console.
 * @constant
 * @type {string}
 */
export const CONSOLE_BASE_URL = '/console/';

const SITE_BASE_URL = CONSOLE_BASE_URL + 'sites/:siteId/';

export const DOCS_BASE_URL = 'https://docs.pbs.org/x/';

/**
 * Constants for route paths and parents if nested
 * @constant
 * @type {object}
 */

export const paths = {
    // GENERAL
    EMPTY_DASHBOARD: CONSOLE_BASE_URL + 'dashboard',
    ADD_SITE: CONSOLE_BASE_URL + 'sites/add',
    LOGOUT: CONSOLE_BASE_URL + 'logout',
    // SITE SPECIFIC
    SITE_DASHBOARD: SITE_BASE_URL + 'dashboard',
    PAGES: SITE_BASE_URL + 'pages',
    ADD_PAGE: SITE_BASE_URL + 'pages/add',
    EDIT_PAGE: SITE_BASE_URL + 'pages/:pageId',
    PAGE_SETTINGS: SITE_BASE_URL + 'pages/:pageId/settings',
    COMPONENT_SETTINGS: SITE_BASE_URL + 'pages/:pageId/components/:componentId',
    LAYOUT_SETTINGS: SITE_BASE_URL + 'pages/:pageId/layouts/:layoutId',
    COMPONENT_LAYOUT_SETTINGS:
        SITE_BASE_URL + 'pages/:pageId/components/:componentId/layouts/:layoutId',
    STYLE: SITE_BASE_URL + 'styles',
    STYLE_PAGE: SITE_BASE_URL + 'styles/:pageId',
    SITE_SETTINGS: SITE_BASE_URL + 'settings',
    FILES: SITE_BASE_URL + 'files',
    FILER: SITE_BASE_URL + 'filer',
    TRASH: SITE_BASE_URL + 'trash',
    REDIRECTS: SITE_BASE_URL + 'redirects'
};

//needed for creating the <<Back link in breadcrumb.jsx
export const breadcrumbs = {
    PAGES: SITE_BASE_URL + 'pages',
    SITE_DASHBOARD: SITE_BASE_URL + 'dashboard',
    EDIT_PAGE: SITE_BASE_URL + 'pages/:pageId',
    PAGE_SETTINGS: SITE_BASE_URL + 'pages/:pageId/settings',
    COMPONENT_SETTINGS: SITE_BASE_URL + 'pages/:pageId/components/:componentId',
    LAYOUT_SETTINGS: SITE_BASE_URL + 'pages/:pageId/layouts/:layoutId',
    STYLE_PAGE: SITE_BASE_URL + 'styles/:pageId'
};

export const menuItems = {
    DASHBOARD: { title: 'Dashboard', path: paths.SITE_DASHBOARD },
    PAGES: { title: 'Pages', path: paths.PAGES },
    FILES: { title: 'Files', path: paths.FILES },
    FILER: { title: 'Filer', path: paths.FILER },
    REDIRECTS: { title: 'Redirects', path: paths.REDIRECTS },
    STYLE: { title: 'Style', path: paths.STYLE },
    SITE_SETTINGS: { title: 'Site Settings', path: paths.SITE_SETTINGS }
};

export const sidebarViews = {
    MAIN_NAV: 'Main Navigation',
    PAGES: 'Pages',
    PAGE_SETTINGS: 'Page Settings',
    COMPONENT_SETTINGS: 'Component Settings'
};

export const mainViews = {
    DASHBOARD: 'Dashboard',
    EDIT_PAGE: 'Edit Page',
    BLOGGER: 'Blogger',
    STYLE: 'Style',
    SITE_SETTINGS: 'Site Settings',
    ADD_SITE: 'Add Site'
};

export const dashboardSections = {
    ADD_NEW_SITE: 'AddNewSite'
};

export const pagesMenuSections = {
    PAGE_TREE: 'PAGE_TREE',
    PAGE_SETTINGS: 'PAGE_SETTINGS'
};

export const apiStatus = {
    NOT_REQUESTED: 'NOT_REQUESTED', // Initial state, may not be used again
    PENDING: 'PENDING', // Preparatory phase, but no request yet
    IN_PROGRESS: 'IN_PROGRESS',
    OK: 'OK',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    FILER_LOAD_FILES: 'FILER_LOAD_FILES'
};

export const pageSection = {
    HEADER: 'header',
    CONTENT: 'contentVersion',
    FOOTER: 'footer',
    RAIL: 'rail'
};

export const sectionType = {
    HEADER: 'header',
    CONTENT: 'content',
    FOOTER: 'footer',
    ERROR: 'error',
    SEARCH: 'search',
    COMPONENT: 'component',
    RAIL: 'rail',
    POPUP: 'popup'
};

export const DEFAULT_LINK = {
    externalHref: '',
    fileId: null,
    pageId: null,
    target: null,
    enabled: false
};

export const EMPTY_EDIT_DATA = {
    layouts: {},
    columns: {},
    components: {},
    section: { layouts: [] }
};

export const COLUMN_TYPES = [
    { colSizes: [12], colTitle: '1 Column' },
    { colSizes: [6, 6], colTitle: '2 Column' },
    { colSizes: [8, 4], colTitle: '2/3 - 1/3 Column' },
    { colSizes: [4, 4, 4], colTitle: '3 Column' },
    { colSizes: [3, 3, 3, 3], colTitle: '4 Column' }
];

export const SITE_TYPE = {
    STATION: { key: 'station', label: 'Station' },
    PRODUCER: { key: 'producer', label: 'Producer' },
    INTERNAL_PBS: { key: 'internal_pbs', label: 'Internal PBS' }
};

export const RAIL_TYPE = {
    LEFT: { key: 'left', label: 'Left' },
    RIGHT: { key: 'right', label: 'Right' }
};

export const SEO_LIMITS = {
    titleLength: 100,
    descriptionLength: 160,
    tagsLength: 255
};

export const ALL_COLUMNS = new Set(
    COLUMN_TYPES.reduce((acc, col) => {
        return [...acc, ...col.colSizes];
    }, [])
);

export const ALL_SECTIONS = new Set(Object.keys(sectionType).map((key) => sectionType[key]));

export const ALL_SITE_TYPES = new Set(Object.keys(SITE_TYPE).map((type) => SITE_TYPE[type].key));

export const PERMISSION_TYPES = {
    add_user: 'add_user',
    change_user: 'change_user',
    delete_user: 'delete_user',
    add_page: 'add_page',
    change_page: 'change_page',
    delete_page: 'delete_page',
    move_page: 'move_page',
    copy_page: 'copy_page',
    add_contentversion: 'add_contentversion',
    change_contentversion: 'change_contentversion',
    delete_contentversion: 'delete_contentversion',
    can_publish_contentversion: 'can_publish_contentversion',
    can_unpublish_contentversion: 'can_unpublish_contentversion',
    add_site: 'add_site',
    change_site: 'change_site',
    add_sitesection: 'add_sitesection',
    change_sitesection: 'change_sitesection',
    delete_sitesection: 'delete_sitesection',
    change_siteVRM: 'change_siteVRM'
};

export const SAMPLE_PAGE_ID = 'sample-page';
export const ADD_PAGE_ID = 'add-page';

export const SPECIAL_PAGE = {
    ERROR_400: 'error_400',
    ERROR_404: 'error_404',
    ERROR_500: 'error_500',
    SEARCH: 'search',
    POPUP: 'popup',
    BLOGS: 'blogs'
};

export const PAGE_TREE_TYPE = {
    FAKE_ROOT: 'fake_root',
    SPECIAL_ROOT: 'special_root',
    BLOGS_ROOT: 'blogs_root',
    POPUP_COLLECTION_ROOT: 'popup_collection_root'
};

export const SITE_FILTERS = {
    ALL_SITES: {
        id: 'ALL_SITES',
        name: 'ALL SITES'
    }
};

export const BENTOMATIC_VAR_TABS = {
    NEW: {
        name: 'NEW',
        label: 'New Variable'
    },
    EXISTING: {
        name: 'EXISTING',
        label: 'Existing Variable'
    }
};

export const operationTypes = {
    PAGE_MOVE: 'page_move',
    PAGE_COPY: 'page_copy',
    PAGE_ADD: 'page_add',
    CUT_LAYOUT: 'CUT_LAYOUT',
    COPY_LAYOUT: 'COPY_LAYOUT',
    CUT_COMPONENT: 'CUT_COMPONENT',
    COPY_COMPONENT: 'COPY_COMPONENT'
};

export const TABS_COMPONENT_ASSETS = {
    SITE_FILES: {
        name: 'SITE_FILES',
        label: 'Site Files'
    },
    MEDIA_MANAGER: {
        name: 'MEDIA_MANAGER',
        label: 'Media Manager'
    }
};

export const APP_NAME = 'Bento 3';

export const VRM_SITE_TYPE = {
    PROXIED: { key: 'proxied', label: 'Proxied' },
    HOSTED: { key: 'hosted', label: 'Hosted' }
};

export const ANALYZE_PERFORMANCE_URL = 'https://pagespeed.web.dev/report?url=';

export const undoActions = {
    ADD_LAYOUT: 'ADD_LAYOUT',
    PASTE_CUT_LAYOUT: 'PASTE_CUT_LAYOUT',
    PASTE_COPIED_LAYOUT: 'PASTE_COPIED_LAYOUT',
    REMOVE_LAYOUT: 'REMOVE_LAYOUT',
    MOVE_LAYOUT: 'MOVE_LAYOUT',
    CHANGE_LAYOUT_TYPE: 'CHANGE_LAYOUT_TYPE',
    CHANGE_LAYOUT_SETTINGS: 'CHANGE_LAYOUT_SETTINGS',
    CHANGE_LAYOUT_SETTINGS_BACKGRUOND: 'CHANGE_LAYOUT_SETTINGS_BACKGRUOND_COLOR',
    CHANGE_LAYOUT_SETTINGS_NAME: 'CHANGE_LAYOUT_SETTINGS_NAME',
    CHANGE_LAYOUT_SETTINGS_SHOWBACKGROUNDCOLOR: 'CHANGE_LAYOUT_SETTINGS_SHOW_BACKGROUND_COLOR',
    CHANGE_LAYOUT_SETTINGS_SHOWBACKGROUNDIMAGE: 'CHANGE_LAYOUT_SETTINGS_SHOW_BACKGROUND_IMAGE',
    CHANGE_LAYOUT_SETTINGS_BACKGROUND_IMAGE: 'CHANGE_LAYOUT_SETTINGS_BACKGROUND_IMAGE',
    CHANGE_LAYOUT_SETTINGS_BACKGROUND_IMAGE_MM: 'CHANGE_LAYOUT_SETTINGS_BACKGROUND_IMAGE_MM',
    CHANGE_LAYOUT_SETTINGS_PADDING: 'CHANGE_LAYOUT_SETTINGS_PADDING',
    CHANGE_LAYOUT_SETTINGS_ISFULLWIDTH: 'CHANGE_LAYOUT_SETTINGS_FULL_WIDTH',
    CHANGE_LAYOUT_SETTINGS_ISFULLHEIGHT: 'CHANGE_LAYOUT_SETTINGS_FULL_HEIGHT',
    CHANGE_LAYOUT_SETTINGS_ISCONTENTVERTICALLYCENTERED:
        'CHANGE_LAYOUT_SETTINGS_VERTICALLY_CENTERED',
    TOGGLE_LAYOUT_DELETE: 'TOGGLE_LAYOUT_DELETE',
    ADD_COLUMN: 'ADD_COLUMN',
    REMOVE_COLUMN: 'REMOVE_COLUMN',
    CHANGE_COLUMN_SIZE: 'CHANGE_COLUMN_SIZE',
    ADD_COMPONENT: 'ADD_COMPONENT',
    REMOVE_COMPONENT: 'REMOVE_COMPONENT',
    REMOVE_COMPONENT_ERROR_BOUNDARY: 'REMOVE_COMPONENT_ERROR_BOUNDARY',
    MOVE_COMPONENT: 'MOVE_COMPONENT',
    CHANGE_COMPONENT_SETTINGS: 'CHANGE_COMPONENT_SETTINGS',
    CHANGE_COMPONENT_DATA: 'CHANGE_COMPONENT_DATA',
    CHANGE_BENTOMATIC_DATA: 'CHANGE_BENTOMATIC_DATA',
    CHANGE_COMPONENT_POSITION: 'CHANGE_COMPONENT_POSITION',
    PASTE_CUT_COMPONENT: 'PASTE_CUT_COMPONENT',
    PASTE_COPIED_COMPONENT: 'PASTE_COPIED_COMPONENT',
    SET_COLUMN_DISPLAY_SIZE: 'SET_COLUMN_DISPLAY_SIZE',
    IMAGE_COMPONENT_CHANGE_ASSET: 'IMAGE_COMPONENT_CHANGE_ASSET',
    IMAGE_COMPONENT_CHANGE_CAPTION: 'IMAGE_COMPONENT_CHANGE_CAPTION',
    IMAGE_COMPONENT_CHANGE_ALT_TEXT: 'IMAGE_COMPONENT_CHANGE_ALT_TEXT',
    IMAGE_COMPONENT_CHANGE_CREDIT: 'IMAGE_COMPONENT_CHANGE_CREDIT',
    IMAGE_COMPONENT_CHANGE_LINK: 'IMAGE_COMPONENT_CHANGE_LINK',
    IMAGE_COMPONENT_CHANGE_SETTING: 'IMAGE_COMPONENT_CHANGE_SETTING',
    VIDEO_COMPONENT_FIRST_ITEM_UPDATE: 'VIDEO_COMPONENT_FIRST_ITEM_UPDATE',
    VIDEO_COMPONENT_PLAYLIST_HEIGHT: 'VIDEO_COMPONENT_PLAYLIST_HEIGHT',
    VIDEO_COMPONENT_DEFAULT: 'VIDEO_COMPONENT_DEFAULT',
    VIDEO_COMPONENT_SET_PLAYLIST: 'VIDEO_COMPONENT_SET_PLAYLIST',
    VIDEO_COMPONENT_SET__NEW_PLAYLIST: 'VIDEO_COMPONENT_SET__NEW_PLAYLIST',
    VIDEO_COMPONENT_SORT: 'VIDEO_COMPONENT_SORT',
    VIDEO_COMPONENT_UPDATE_PLAYLIST_ITEM: 'VIDEO_COMPONENT_UPDATE_PLAYLIST_ITEM',
    VIDEO_COMPONENT_REMOVE: 'VIDEO_COMPONENT_REMOVE',
    VIDEO_COMPONENT_SETTINGS_TOGGLE: 'VIDEO_COMPONENT_SETTINGS_TOGGLE',
    VIDEO_COMPONENT_SETTINGS_PLAYLIST_HEIGHT: 'VIDEO_COMPONENT_SETTINGS_PLAYLIST_HEIGHT',
    VIDEO_LIST_COMPONENT_SETTINGS_CHANGE_SHOW: 'VIDEO_LIST_COMPONENT_SETTINGS_CHANGE_SHOW',
    VIDEO_LIST_COMPONENT_SETTINGS_SET_ITEMS_PER_PAGE:
        'VIDEO_LIST_COMPONENT_SETTINGS_SET_ITEMS_PER_PAGE',
    VIDEO_LIST_COMPONENT_SETTINGS_TOOGLE: 'VIDEO_LIST_COMPONENT_SETTINGS_TOOGLE',
    VIDEO_LIST_COMPONENT_SETTINGS_CHANGE: 'VIDEO_LIST_COMPONENT_SETTINGS_CHANGE',
    VIDEO_LIST_COMPONENT_SETTINGS_ORDER: 'VIDEO_LIST_COMPONENT_SETTINGS_ORDER',
    AUDIO_PLAYER_SET_IMG_URL: 'AUDIO_PLAYER_SET_IMG_URL',
    AUDIO_PLAYER_SET_IMG_ALT: 'AUDIO_PLAYER_SET_IMG_ALT',
    AUDIO_PLAYER_SET_IMG: 'AUDIO_PLAYER_SET_IMG',
    AUDIO_PLAYER_SET_STATION: 'AUDIO_PLAYER_SET_STATION',
    AUDIO_PLAYER_SET_TITLE: 'AUDIO_PLAYER_SET_TITLE',
    AUDIO_PLAYER_SETTINGS_URL: 'AUDIO_PLAYER_SETTINGS_URL',
    AUDIO_PLAYER_SETTINGS_SHOW_STATION: 'AUDIO_PLAYER_SETTINGS_SHOW_STATION',
    AUDIO_PLAYER_SETTINGS_SHOW_IMAGE: 'AUDIO_PLAYER_SETTINGS_SHOW_IMAGE',
    AUDIO_PLAYER_SETTINGS_ALT_TEXT: 'AUDIO_PLAYER_SETTINGS_ALT_TEXT',
    CODE_COMPONENT_CHANGE_CONTENT: 'CODE_COMPONENT_CHANGE_CONTENT',
    SOCIAL_ADD_NEW_ICON: 'SOCIAL_ADD_NEW_ICON',
    SOCIAL_CHANGE_LINK: 'SOCIAL_CHANGE_LINK',
    SOCIAL_DELETE_ICON: 'SOCIAL_DELETE_ICON',
    SOCIAL_TEXT: 'SOCIAL_TEXT',
    SOCIAL_SORT: 'SOCIAL_SORT',
    SOCIAL_SETTINGS_COLOR_THEME: 'SOCIAL_SETTINGS_COLOR_THEME',
    SOCIAL_SETTINGS_SHOW_ICON_BORDER: 'SOCIAL_SETTINGS_SHOW_ICON_BORDER',
    SOCIAL_SETTINGS_SHOW_TEXT: 'SOCIAL_SETTINGS_SHOW_TEXT',
    SOCIAL_SETTINGS_ALIGN: 'SOCIAL_SETTINGS_ALIGN',
    FACEBOOK_LIKE_TITLE: 'FACEBOOK_LIKE_TITLE',
    FACEBOOK_LIKE_SETTINGS_RADIO: 'FACEBOOK_LIKE_SETTINGS_RADIO',
    FACEBOOK_LIKE_SETTINGS_TOGGLE: 'FACEBOOK_LIKE_SETTINGS_TOGGLE',
    FACEBOOK_LIKE_SETTINGS_PAGE_NAME: 'FACEBOOK_LIKE_SETTINGS_PAGE_NAME',
    FACEBOOK_LIKE_SETTINGS_SELECT_LAYOUT: 'FACEBOOK_LIKE_SETTINGS_SELECT_LAYOUT',
    TWITTER_FOLLOW_SET_DATA: 'TWITTER_FOLLOW_SET_DATA',
    TWITTER_FOLLOW_SETTINGS_RADIO: 'TWITTER_FOLLOW_SETTINGS_RADIO',
    TWITTER_FOLLOW_SETTINGS_TOGGLE: 'TWITTER_FOLLOW_SETTINGS_TOGGLE',
    TWITTER_FOLLOW_SETTINGS_SCREEN_NAME: 'TWITTER_FOLLOW_SETTINGS_SCREEN_NAME',
    CODE_COMPONENT_CHANGE_LABEL: 'CODE_COMPONENT_CHANGE_LABEL',
    CODE_COMPONENT_CHANGE_ENABLE: 'CODE_COMPONENT_CHANGE_ENABLE',
    TEXT_COMPONENT_CHANGE_CONTENT: 'TEXT_COMPONENT_CHANGE_CONTENT',
    TEXT_COMPONENT_CHANGE_SETTINGS_BACKGROUND: 'TEXT_COMPONENT_CHANGE_SETTINGS_BACKGROUND',
    TEXT_COMPONENT_CHANGE_SETTINGS_PADDING: 'TEXT_COMPONENT_CHANGE_SETTINGS_PADDING',
    TEXT_COMPONENT_CHANGE_SETTINGS_MAX_WIDTH: 'TEXT_COMPONENT_CHANGE_SETTINGS_MAX_WIDTH',
    LINKS_COMPONENT_NEW_ITEM: 'LINKS_COMPONENT_NEW_ITEM',
    LINKS_COMPONENT_CHANGE: 'LINKS_COMPONENT_CHANGE',
    LINKS_COMPONENT_DELETE: 'LINKS_COMPONENT_DELETE',
    LINKS_COMPONENT_SORT: 'LINKS_COMPONENT_SORT',
    LINKS_COMPONENT_SET_TITLE: 'LINKS_COMPONENT_SET_TITLE',
    SIMPLE_BUTTON_COMPONENT_LINK: 'SIMPLE_BUTTON_COMPONENT_LINK',
    SIMPLE_BUTTON_COMPONENT_CHANGE: 'SIMPLE_BUTTON_COMPONENT_CHANGE',
    STATION_HEADER_SET_LOGO: 'STATION_HEADER_SET_LOGO',
    STATION_HEADER_SET_LOGO_MM: 'STATION_HEADER_SET_LOGO_MM',
    STATION_HEADER_SETTINGS_ISSTICKY: 'STATION_HEADER_SETTINGS_ISSTICKY',
    STATION_HEADER_SETTINGS_HASHIGHLIGHT: 'STATION_HEADER_SETTINGS_HASHIGHLIGHT',
    STATION_HEADER_SETTINGS_SHOWLOGO: 'STATION_HEADER_SETTINGS_SHOWLOGO',
    STATION_HEADER_SETTINGS_SHOWSEARCH: 'STATION_HEADER_SETTINGS_SHOWSEARCH',
    STATION_HEADER_SETTINGS_SHOWLIVE: 'STATION_HEADER_SETTINGS_SHOWLIVE',
    STATION_HEADER_SETTINGS_LIVELINKURL: 'STATION_HEADER_SETTINGS_LIVELINKURL',
    STATION_HEADER_SETTINGS_LOGOALTTEXT: 'STATION_HEADER_SETTINGS_LOGOALTTEXT',
    STATION_HEADER_SETTINGS_SEARCHPLACEHOLDER: 'STATION_HEADER_SETTINGS_SEARCHPLACEHOLDER',
    MENU_COMMON_ADD_ITEM: 'MENU_COMMON_ADD_ITEM',
    MENU_COMMON_ADD_SUBITEM: 'MENU_COMMON_ADD_SUBITEM',
    MENU_COMMON_DELETE_ITEM: 'MENU_COMMON_DELETE_ITEM',
    MENU_COMMON_SET_ITEM: 'MENU_COMMON_SET_ITEM',
    MENU_COMMON_SORT_ITEM: 'MENU_COMMON_SORT_ITEM',
    MENU_COMMON_SORT_SUB_ITEMS: 'MENU_COMMON_SORT_SUB_ITEMS',
    MENU_COMMON_CURRENT_INDEX: 'MENU_COMMON_CURRENT_INDEX',
    JAWS_COMPONENT_SETTINGS_API: 'JAWS_COMPONENT_SETTINGS_API',
    FLEXIBLE_FOOTER_NEW_ITEM: 'FLEXIBLE_FOOTER_NEW_ITEM',
    FLEXIBLE_FOOTER_CHANGE_ITEM: 'FLEXIBLE_FOOTER_CHANGE_ITEM',
    FLEXIBLE_FOOTER_DELETE_ITEM: 'FLEXIBLE_FOOTER_DELETE_ITEM',
    FLEXIBLE_FOOTER_SORT_ITEMS: 'FLEXIBLE_FOOTER_SORT_ITEMS',
    FLEXIBLE_FOOTER_SET_LOGO_IMG: 'FLEXIBLE_FOOTER_SET_LOGO_IMG',
    FLEXIBLE_FOOTER_SET_LOGO_LINK: 'FLEXIBLE_FOOTER_SET_LOGO_LINK',
    FLEXIBLE_FOOTER_SET_LOGO: 'FLEXIBLE_FOOTER_SET_LOGO',
    FLEXIBLE_FOOTER_SETTINGS_SHOWDETAILS: 'FLEXIBLE_FOOTER_SETTINGS_SHOWDETAILS',
    FLEXIBLE_FOOTER_SETTINGS_SHOWSOCIAL: 'FLEXIBLE_FOOTER_SETTINGS_SHOWSOCIAL',
    FLEXIBLE_FOOTER_SETTINGS_SHOWCOPYRIGHT: 'FLEXIBLE_FOOTER_SETTINGS_SHOWCOPYRIGHT',
    FLEXIBLE_FOOTER_SETTINGS_SHOWLOGO: 'FLEXIBLE_FOOTER_SETTINGS_SHOWLOGO',
    FLEXIBLE_FOOTER_SETTINGS_SHOW_HEADER: 'FLEXIBLE_FOOTER_SETTINGS_SHOW_HEADER',
    FLEXIBLE_FOOTER_SETTINGS_TYPE_COMPACT: 'FLEXIBLE_FOOTER_SETTINGS_TYPE_COMPACT',
    FLEXIBLE_FOOTER_SETTINGS_TYPE_EXPANDED: 'FLEXIBLE_FOOTER_SETTINGS_TYPE_EXPANDED',
    FLEXIBLE_FOOTER_SETTINGS_BACKGROUND_LIGHT: 'FLEXIBLE_FOOTER_SETTINGS_BACKGROUND_LIGHT',
    FLEXIBLE_FOOTER_SETTINGS_BACKGROUND_DARK: 'FLEXIBLE_FOOTER_SETTINGS_BACKGROUND_DARK',
    FLEXIBLE_FOOTER_SETTINGS_LOGOALTTEXT: 'FLEXIBLE_FOOTER_SETTINGS_LOGOALTTEXT',
    EXPANDED_FOOTER_SET_DETAILS: 'EXPANDED_FOOTER_SET_DETAILS',
    EXPANDED_FOOTER_NEW_ITEM: 'EXPANDED_FOOTER_NEW_ITEM',
    EXPANDED_FOOTER_CHANGE_LINK: 'EXPANDED_FOOTER_CHANGE_LINK',
    EXPANDED_FOOTER_CHANGE_HEADER: 'EXPANDED_FOOTER_CHANGE_HEADER',
    EXPANDED_FOOTER_DELETE_ITEM: 'EXPANDED_FOOTER_DELETE_ITEM',
    EXPANDED_FOOTER_SORT_ITEMS: 'EXPANDED_FOOTER_SORT_ITEMS',
    BLOG_ENTRY_SETTINGS_NEW_TAG: 'BLOG_ENTRY_SETTINGS_NEW_TAG',
    BLOG_ENTRY_SETTINGS_TAGS: 'BLOG_ENTRY_SETTINGS_TAGS',
    BLOG_ENTRY_SETTINGS_TOOGLE: 'BLOG_ENTRY_SETTINGS_TOOGLE',
    BLOG_ENTRY_ALT_TEXT: 'BLOG_ENTRY_ALT_TEXT',
    BLOG_ENTRY_IMAGE_DATA: 'BLOG_ENTRY_IMAGE_DATA',
    BLOG_ENTRY_IMAGE_SETTINGS: 'BLOG_ENTRY_IMAGE_SETTINGS',
    BLOG_ENTRY_PUBLICATION_DATE: 'BLOG_ENTRY_PUBLICATION_DATE',
    BLOG_ENTRY_NEW_AUTHOR: 'BLOG_ENTRY_NEW_AUTHOR',
    BLOG_ENTRY_UPDATE_DATA: 'BLOG_ENTRY_UPDATE_DATA',
    BLOG_ENTRY_SELECT_AUTHOR: 'BLOG_ENTRY_SELECT_AUTHOR',
    BLOG_ENTRY_CONTENT: 'BLOG_ENTRY_CONTENT',
    MENU_COLUMN_CHANGE: 'MENU_COLUMN_CHANGE',
    MENU_PROMO_SET_IMG_LINK: 'MENU_PROMO_SET_IMG_LINK',
    MENU_PROMO_SET_DETAILS: 'MENU_PROMO_SET_DETAILS',
    MENU_PROMO_SET_DATA: 'MENU_PROMO_SET_DATA',
    MENU_PROMO_NEW_IMG: 'MENU_PROMO_NEW_IMG',
    MENU_PROMO_NEW_IMG_MM: 'MENU_PROMO_NEW_IMG_MM',
    MENU_PROMO_TITLE: 'MENU_PROMO_TITLE',
    MENU_SETTINGS_SET_ALIGNMENT: 'MENU_SETTINGS_ALIGNMENT',
    MENU_SETTINGS_SET_DISPLAYTYPE: 'MENU_SETTINGS_DISPLAYTYPE',
    MENU_SETTINGS_SET_SHOWTITLE: 'MENU_SETTINGS_SET_SHOWTITLE',
    MENU_SETTINGS_SET_SHOWDETAILS: 'MENU_SETTINGS_SET_SHOWDETAILS',
    MENU_PROMO_SETTINGS_ALT_TEXT: 'MENU_PROMO_SETTINGS_ALT_TEXT',
    PAGE_HEADER_SETTINGS_BACKGROUND_COLOR: 'PAGE_HEADER_SETTINGS_BACKGROUND_COLOR',
    PAGE_HEADER_SETTINGS_TEXT_COLOR: 'PAGE_HEADER_SETTINGS_TEXT_COLOR',
    PAGE_HEADER_SETTINGS_SHOWONHOME: 'PAGE_HEADER_SETTINGS_SHOWONHOME',
    PAGE_HEADER_SETTINGS_SHOWPROGRAMLOGO: 'PAGE_HEADER_SETTINGS_SHOWPROGRAMLOGO',
    PAGE_HEADER_SETTINGS_SHOWPAGETITLE: 'PAGE_HEADER_SETTINGS_SHOWPAGETITLE',
    PAGE_HEADER_SETTINGS_SHOWSOCIALICONS: 'PAGE_HEADER_SETTINGS_SHOWSOCIALICONS',
    PAGE_HEADER_SETTINGS_SHOWBACKGROUNDIMAGE: 'PAGE_HEADER_SETTINGS_SHOWBACKGROUNDIMAGE',
    PAGE_HEADER_SETTINGS_TEXT: 'PAGE_HEADER_SETTINGS_TEXT',
    SECTION_HEADER_SETTINGS_RESET_COLORS: 'SECTION_HEADER_SETTINGS_RESET_COLORS',
    SECTION_HEADER_SETTINGS_SECONDARY_COLOR: 'SECTION_HEADER_SETTINGS_SECONDARY_COLOR',
    SECTION_HEADER_SETTINGS_TEXT_COLOR: 'SECTION_HEADER_SETTINGS_TEXT_COLOR',
    SECTION_HEADER_SETTINGS_TAG: 'SECTION_HEADER_SETTINGS_TAG',
    SECTION_HEADER_HEADING: 'SECTION_HEADER_HEADING',
    PAGE_HEADER_PAGE_TITLE: 'PAGE_HEADER_PAGE_TITLE',
    PAGE_HEADER_NEW_IMAGE: 'PAGE_HEADER_NEW_IMAGE',
    PAGE_HEADER_HEADER_MM: 'PAGE_HEADER_HEADER_MM',
    SECTION_HEADER_TEXT: 'SECTION_HEADER_TEXT'
};
